<template>
  <Flyout
    v-on-clickaway="handleClose"
    :data-test-id="$testId('flyout')"
    isOpen
    @close="handleClose"
  >
    <template v-slot:title>
      <div>
        <HeadingMedium
          class="flyoutTitle"
          element="h2"
        >
          <Icon
            :data-test-id="$testId('icon')"
            icon="whatsNew"
            :scale="1.25"
          />
          <CountBadge
            :ariaLabel="ariaLabel"
            class="whatsNewCountBadge"
            :count="unreadWhatsNewItemsCount"
            :data-test-id="$testId('countBadge')"
          >
            <span
              class="buttonLabel"
              :data-test-id="$testId('buttonLabel')"
            >
              What's New
            </span>
          </CountBadge>
        </HeadingMedium>
      </div>
    </template>

    <template v-slot:body>
      <WhatsNewListEmpty
        v-if="errorFetching"
        :data-test-id="$testId('fetchingError')"
        headerText="Give us a moment"
        imagePath="img/resourcesAside/whatsnew/sadCloud.svg"
        :subtext="$options.errorSubtext"
      />
      <WhatsNewListEmpty
        v-else-if="!whatsNewItems.length"
        :data-test-id="$testId('emptyList')"
        headerText="Nothing new here..."
        imagePath="img/resourcesAside/whatsnew/workingMan.svg"
        :subtext="$options.emptySubtext"
      />
      <ul v-else>
        <li
          v-for="item in whatsNewItems"
          :key="item.id"
        >
          <WhatsNewItem v-bind="item" />
        </li>
      </ul>
    </template>
  </Flyout>
</template>

<script>
import {
  CountBadge,
  HeadingMedium,
  Icon,
} from '@jumpcloud/ui-components';

import { mapActions, mapGetters, mapState } from 'vuex';
import { directive as onClickaway } from 'vue-clickaway';
import Flyout from 'client/components/Flyout.vue';
import UserApi from 'client/api/v2/UserApi';
import WhatsNewItem from 'client/components/WhatsNew/WhatsNewItem.vue';
import WhatsNewListEmpty from 'client/components/WhatsNew/WhatsNewListEmpty.vue';
import moment from 'moment';
import pluralize from 'pluralize';

const knowledgeModule = 'KnowledgeModule';
const alertModule = 'AlertModule';

export default {
  name: 'WhatsNewOverlay',
  components: {
    CountBadge,
    Flyout,
    HeadingMedium,
    Icon,
    WhatsNewListEmpty,
    WhatsNewItem,
  },

  emptySubtext: 'We\'re working hard to improve the product. Check back later to see what new things we\'ve built for you.',
  errorSubtext: 'It seems we\'re experiencing a problem. Check back later to see if there is anything new.',

  directives: {
    onClickaway,
  },

  emits: ['close'],

  computed: {
    ...mapState(knowledgeModule, [
      'whatsNewItems',
      'errorFetching',
    ]),

    ...mapGetters(knowledgeModule, [
      'unreadWhatsNewItemsCount',
    ]),

    ariaLabel() {
      return this.unreadWhatsNewItemsCount > 0
        ? `What's New badge shows ${this.unreadWhatsNewItemsCount} unread ${pluralize('message', this.unreadWhatsNewItemsCount)}.`
        : '';
    },
  },

  watch: {
    whatsNewItems: {
      deep: true,
      handler() {
        this.handleMarkArticlesAsRead();
      },
    },
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },

  created() {
    this.handleMarkArticlesAsRead();
  },

  methods: {
    ...mapActions(knowledgeModule, [
      'removeResourcesAlert',
      'setAllUnreadToFalse',
    ]),

    ...mapActions(alertModule, [
      'triggerErrorAlert',
    ]),

    handleBeforeDestroy() {
      this.setAllUnreadToFalse();
    },

    handleClose() {
      this.$emit('close', 'Resources - What\'s New');
    },

    handleMarkArticlesAsRead() {
      if (this.whatsNewItems[0]?.unread) {
        const latestArticlePublishedDate = moment(this.whatsNewItems[0].publishedDate);
        UserApi.putSelf({ lastWhatsNewChecked: latestArticlePublishedDate })
          .catch(() => {
            this.triggerErrorAlert('Unable to remove What\'s New notification');
          });
        this.removeResourcesAlert();
      }
    },
  },
};
</script>

<style scoped>
.buttonLabel {
  margin-right: var(--jc-spacer);
}

.flyoutTitle {
  align-items: center;
  display: flex;
  gap: 12px;
}

li {
  padding: 0;
}

ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.whatsNewCountBadge {
  align-items: center;
}
</style>
